/**
 * Builds the `updateCompanyMetaData` input by merging the current company snapshot
 * with the fields being changed. The backend merge logic may assume existing meta
 * fields are present; sending only a delta (e.g. `{ businessDescription }`) can cause
 * server errors when other meta keys are missing on the merged object.
 */
export type CompanyLikeForMetaUpdate = {
  country?: string | null
  links?: { website?: string | null } | null
  meta?: {
    industry?: string | null
    businessDescription?: string | null
  } | null
}

function trimStr(v: unknown): string {
  if (v == null) return ''
  return String(v).trim()
}

export function mergeCompanyMetaUpdateInput(
  company: CompanyLikeForMetaUpdate | null | undefined,
  partial: Record<string, unknown>
): Record<string, string> {
  const fromCompany: Record<string, string> = {}
  const meta = company?.meta

  const industry = trimStr(meta?.industry)
  if (industry) fromCompany.industry = industry

  const desc = trimStr(meta?.businessDescription)
  if (desc) fromCompany.businessDescription = desc

  const site = trimStr(company?.links?.website)
  if (site) fromCompany.websiteUrl = site

  const country = trimStr(company?.country)
  if (country) fromCompany.country = country.toLowerCase()

  const incoming: Record<string, string> = {}
  for (const [k, v] of Object.entries(partial)) {
    if (v == null) continue
    const raw = typeof v === 'string' ? v : String(v)
    const s = raw.trim()
    if (k === 'website' || k === 'websiteUrl') {
      if (s !== '') incoming.websiteUrl = s
    } else if (k === 'country' && s !== '') {
      incoming.country = s.toLowerCase()
    } else if (s !== '') {
      incoming[k] = s
    }
  }

  return { ...fromCompany, ...incoming }
}
